import { cond, head, isEqual, last, stubTrue } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { MapEmbedding, MapShape } from '@/feature/map'
import {
  GEO_FEATURE_MARKER_COLOR,
  GEOFENCE_FIELDS,
  HIGH_RISK_MARKER_COLOR,
  useObservatoryPageStore,
} from '@/feature/observatory'

import { GeoFenceTarget } from '../../GeoFenceCreateEdit/components/GeoFenceTarget'
import styles from '../../GeoFenceCreateEdit/GeoFenceCreateEdit.module.css'
import { GeoFenceShapeFieldProps } from '../../GeoFenceCreateEdit/GeoFenceCreateEdit.types'
import { useReverseGeocodedPosition, useUpdateAddressFromSearch } from '../../hooks'

type PolygonGeoFenceShapeProps = GeoFenceShapeFieldProps & { isEditing?: boolean }

export const PolygonGeoFenceShape = ({
  setSimulatedPosition,
  isEditing = false,
}: PolygonGeoFenceShapeProps) => {
  const { watch, control } = useFormContext()
  const map = useObservatoryPageStore((state) => state.map?.map)

  const setShowSearchMarker = useObservatoryPageStore((state) => state.setShowSearchMarker)

  const { replace } = useFieldArray({
    control,
    name: GEOFENCE_FIELDS.points,
  })

  const setPolygon = (points: google.maps.LatLng[]) => {
    replace(points.map((p) => p.toJSON()))
  }

  const polygon: google.maps.LatLngLiteral[] = watch(GEOFENCE_FIELDS.points) ?? []

  const highRisk = watch(GEOFENCE_FIELDS.highRisk)

  let radiusShapeColor = '#1945B64D'
  if (highRisk) {
    radiusShapeColor = '#FF000040'
  }
  const contactPoint = last(polygon)

  const [isClosed, setIsClosed] = useState(
    polygon.length > 2 && (isEditing || isEqual(head(polygon), last(polygon))),
  )

  const lineRef = useRef<google.maps.Polyline | null>(null)
  const infoRef = useRef<HTMLDivElement | null>(null)

  const lastMousePosition = useRef<google.maps.LatLngLiteral | null>(null)

  const polygonRef = useRef<google.maps.Polygon | null>(null)
  const polylineRef = useRef<google.maps.Polyline | null>(null)

  useUpdateAddressFromSearch(setSimulatedPosition)

  useEffect(() => {
    if (map) {
      const clickListener = map?.addListener('click', (e: google.maps.MapMouseEvent) => {
        const poly = polygonRef.current
        if (poly) {
          if (poly.getPath()) {
            polygonRef.current?.getPath()?.push(e.latLng!)
          } else {
            polygonRef.current?.setPath([e.latLng!])
          }
        }
      })

      return () => {
        clickListener?.remove()
      }
    }
  }, [map])
  const mouseMove = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      lastMousePosition.current = e.latLng.toJSON()
      lineRef.current?.setPath([last(polygonRef.current?.getPath()?.getArray())!, e.latLng])
    }
    const domEvent = e.domEvent
    if (domEvent instanceof MouseEvent) {
      infoRef.current?.style.setProperty('top', `${domEvent.clientY}px`)
      infoRef.current?.style.setProperty('left', `${domEvent.clientX}px`)
    }
  }, [])

  const updateFromPosition = useReverseGeocodedPosition()

  useEffect(() => {
    const poly = polygonRef.current
    if (poly) {
      const updatePolygon = () => {
        polylineRef.current?.setPath(poly.getPath().getArray())
        setPolygon(poly.getPath().getArray())

        if (isClosed) {
          const center = poly
            .getPath()
            .getArray()
            .reduce((a, p) => a.extend(p), new window.google.maps.LatLngBounds())
            .getCenter()
          updateFromPosition(center)
        }
      }

      const listeners = [
        !isClosed
          ? poly.addListener('click', (e: google.maps.PolyMouseEvent) => {
              const closes = e.vertex === 0 && poly.getPath().getLength() > 2
              setIsClosed(closes)

              if (closes) {
                const center = poly
                  .getPath()
                  .getArray()
                  .reduce((a, p) => a.extend(p), new window.google.maps.LatLngBounds())
                  .getCenter()
                updateFromPosition(center)
              }
            })
          : undefined,
        poly.addListener('mousemove', mouseMove),
        poly.addListener('dragend', updatePolygon),
        poly.getPath()?.addListener('set_at', updatePolygon),
        poly.getPath()?.addListener('insert_at', updatePolygon),
        poly.getPath()?.addListener('remove_at', updatePolygon),
      ]

      return () => {
        listeners.forEach((l) => l?.remove?.())
      }
    }
  }, [polygonRef.current, polygonRef.current?.getPath(), isClosed])

  useEffect(() => {
    const mapDiv = map?.getDiv()
    if (mapDiv) {
      mapDiv.classList.add(styles.mapPointer)

      return () => {
        mapDiv.classList.remove(styles.mapPointer)
      }
    }
  }, [])
  useEffect(() => {
    const moveListener = map?.addListener('mousemove', mouseMove)
    return () => {
      moveListener?.remove()
    }
  }, [map, contactPoint, contactPoint ? lineRef.current : infoRef.current])

  useEffect(() => {
    setShowSearchMarker(false)
    return () => {
      setShowSearchMarker(true)
    }
  }, [])

  const auxRender = cond([
    [
      () => isClosed,
      () => (
        <GeoFenceTarget
          isDraggable={false}
          onMove={(v) => {
            if (v) {
              setSimulatedPosition({ address: '', location: v.toJSON() })
            }
          }}
        />
      ),
    ],
    [
      () => !!contactPoint,
      () => (
        <MapShape
          shape={window.google.maps.Polyline}
          map={map}
          ref={lineRef}
          parameters={{
            map,
            paths: lastMousePosition.current ? [contactPoint, lastMousePosition.current] : [],
            strokeColor: '#000',
            strokeWeight: 1,
            geodesic: true,
            clickable: false,
          }}
        />
      ),
    ],
    [
      stubTrue,
      () => (
        <MapEmbedding position={window.google.maps.ControlPosition.TOP_CENTER} map={map}>
          <div
            ref={infoRef}
            className={styles.polygonEditChaser}
            data-testid="geofence-polygon-chaser"
          >
            Select starting point
          </div>
        </MapEmbedding>
      ),
    ],
  ])

  return (
    <>
      <MapShape
        ref={polygonRef}
        shape={window.google.maps.Polygon}
        map={map}
        parameters={{
          map,
          paths: [polygon],
          strokeColor: highRisk ? HIGH_RISK_MARKER_COLOR : GEO_FEATURE_MARKER_COLOR,
          strokeWeight: isClosed ? 3 : 0,
          fillColor: radiusShapeColor,
          fillOpacity: 1,
          editable: true,
          draggable: true,
          geodesic: true,
        }}
      />

      {!isClosed ? (
        <MapShape
          ref={polylineRef}
          shape={window.google.maps.Polyline}
          map={map}
          parameters={{
            map,
            path: polygonRef?.current?.getPath()?.getArray() ?? polygon,
            strokeColor: highRisk ? HIGH_RISK_MARKER_COLOR : GEO_FEATURE_MARKER_COLOR,
            strokeWeight: 3,
            geodesic: true,
            clickable: false,
          }}
        />
      ) : null}

      {auxRender()}
    </>
  )
}
